const REPORT_GAPS_MS = [1, 2, 4, 8, 10].map(minutes => minutes * 60_000);

type ReportOptions = Readonly<{ beacon: boolean }>;

export type EngagementReporter = Readonly<{
  report: () => void;
  stop: () => void;
}>;

// Time spent goes out in few events: on a widening schedule while it accrues, and at once by beacon when the
// page hides, so closing the tab still counts. Pausing, seeking or re-rendering only adds to the next report.
export function createEngagementReporter<T>({
  collect,
  send,
}: Readonly<{
  collect: () => T;
  send: (accrued: NonNullable<T>, options: ReportOptions) => void;
}>): EngagementReporter {
  let reports = 0;
  let timeout: ReturnType<typeof setTimeout> | undefined;

  const report = (beacon: boolean) => {
    const accrued = collect();
    if (accrued) send(accrued, { beacon });
  };
  const schedule = () => {
    timeout = setTimeout(
      () => {
        report(false);
        reports += 1;
        schedule();
      },
      REPORT_GAPS_MS[Math.min(reports, REPORT_GAPS_MS.length - 1)],
    );
  };
  const handlePageHide = (event: Event) => {
    if (event.type === 'pagehide' || document.visibilityState === 'hidden') report(true);
  };

  schedule();
  document.addEventListener('visibilitychange', handlePageHide);
  window.addEventListener('pagehide', handlePageHide);

  return {
    report: () => report(false),
    stop: () => {
      clearTimeout(timeout);
      document.removeEventListener('visibilitychange', handlePageHide);
      window.removeEventListener('pagehide', handlePageHide);
      report(false);
    },
  };
}
